import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { StatusTone } from '@bookingdei/i18n/status-tone';
import './toast.css';

// Toast + stack (CB-17; design M:6525-6527): ink rectangle (`--toast-bg`), radius 8,
// 14 px 600, `toastin .18s ease-out`, stacked bottom-centre 88 px above the viewport
// edge (clear of the floating mobile bar) plus the safe-area inset. Toasts sit above
// every layer and never take focus; they announce through a polite live region.
export interface ToastOptions {
  tone?: StatusTone;
  /** Auto-dismiss after this many ms (default 3200). 0 keeps it until dismissed. */
  duration?: number;
}

interface ToastItem extends Required<ToastOptions> {
  id: number;
  message: string;
}

interface ToastContextValue {
  show: (message: string, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setItems((list) => list.filter((i) => i.id !== id));
  }, []);

  const show = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const id = nextId.current++;
      const item: ToastItem = {
        id,
        message,
        tone: opts.tone ?? 'neutral',
        duration: opts.duration ?? 3200,
      };
      setItems((list) => [...list, item].slice(-MAX_VISIBLE));
      if (item.duration > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), item.duration),
        );
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className={`toast toast--${i.tone}`} onClick={() => dismiss(i.id)}>
            {i.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
